'use client';

// «ОБНОВИТЕ ПРИЛОЖЕНИЕ» — блокирующее окно для сборок ниже минимальной (ТЗ релиза П2,
// владелец 29.09.2026). Работает на уже выпущенных сборках: только веб и существующие
// методы моста. Порог и ссылка — константы lib/app-release.ts (без админки и БД); свежие
// значения приезжают с тем же ответом /api/version, что сверяет PwaUpdater (второго
// опроса сервера нет — lib/version-poll).
//
// Показ: сборка с родным окном `notice` — низкий лист с кнопкой «Обновить»; лист закрыли —
// через 3 с он появляется снова, пока сборка ниже порога. Сборки без `notice` —
// веб-оверлей на весь экран поверх кабинета. В браузере гейта нет.
import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { KButton } from '@scriptera/ui/kit';
import { FCard, FHint } from '@/components/cabinet/form-kit';
import { showNativeNotice } from '@/components/cabinet/confirm-sheet';
import { IOS_MIN_BUILD, IOS_STORE_URL, shouldGate } from '@/lib/app-release';
import { log } from '@/lib/client-journal';
import { report } from '@/lib/client-report';
import { isNativeApp, nativeBuildNumber, nativeMethod } from '@/lib/native-app';
import { lastVersion, onVersion } from '@/lib/version-poll';

/** Пауза перед повторным показом закрытого листа. */
const REPEAT_MS = 3000;

interface Gate {
  build: number;
  min: number;
  url: string;
  /** Сборка без родного окна `notice` — веб-оверлей вместо листа. */
  web: boolean;
}

function openStore(url: string): void {
  if (!url) return;
  try {
    window.open(url, '_blank');
  } catch {
    /* ссылка не открылась — окно останется, человек нажмёт ещё раз */
  }
}

export function AppUpdateGate() {
  const t = useTranslations('appUpdate');
  const [gate, setGate] = useState<Gate | null>(null);
  const reported = useRef(false);

  // Порог: сначала зашитый в бандл, дальше — каждый ответ /api/version (сервер — истина).
  useEffect(() => {
    if (!isNativeApp()) return;
    const build = nativeBuildNumber() ?? 0;
    const web = !nativeMethod('notice');
    const apply = (min: number, url: string) => {
      setGate(shouldGate(build, min) ? { build, min, url, web } : null);
    };
    const known = lastVersion();
    if (known) apply(known.iosMinBuild, known.iosStoreUrl);
    else apply(IOS_MIN_BUILD, IOS_STORE_URL);
    return onVersion((v) => apply(v.iosMinBuild, v.iosStoreUrl));
  }, []);

  const active = gate !== null;
  const min = gate?.min ?? 0;
  const build = gate?.build ?? 0;
  const url = gate?.url ?? '';
  const webOverlay = gate?.web ?? false;

  // Журнал и центр ошибок — один раз за загрузку страницы (вид тихий, владельца не будит).
  useEffect(() => {
    if (!active || reported.current) return;
    reported.current = true;
    log('app.update.gate', { build, min });
    report('client_app_outdated', `app build ${build} < min ${min}`, undefined, { appBuild: build, min });
  }, [active, build, min]);

  // Родной лист по кругу, пока сборка ниже порога; без метода — веб-оверлей.
  useEffect(() => {
    if (!active || webOverlay) return;
    let alive = true;
    let timer: number | null = null;
    const show = () => {
      if (!alive) return;
      void showNativeNotice({ style: 'sheet', title: t('title'), text: t('text'), primary: t('button') }).then(
        (ok) => {
          if (!alive) return;
          if (ok) openStore(url);
          timer = window.setTimeout(show, REPEAT_MS);
        },
      );
    };
    show();
    return () => {
      alive = false;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [active, webOverlay, url, t]);

  if (!active || !webOverlay) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-update-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483000,
        background: 'var(--bg)',
        color: 'var(--ink)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'calc(env(safe-area-inset-top) + 16px) 16px calc(env(safe-area-inset-bottom) + 16px)',
      }}
    >
      <FCard>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 360 }}>
          <h2 id="app-update-title" style={{ margin: 0 }}>
            {t('title')}
          </h2>
          <FHint>{t('text')}</FHint>
          <KButton tone="primary" block onClick={() => openStore(url)}>
            {t('button')}
          </KButton>
        </div>
      </FCard>
    </div>
  );
}
